/**
 * Sovereign Platform Support API Client
 * Wraps @homy/api-client platformClient for Track 2 Platform Support endpoints.
 */

import { platformClient } from "@homy/api-client";

/**
 * Normalize support ticket status from client variations (e.g. WAITING -> WAITING_CUSTOMER).
 * @param {string|string[]} status
 * @returns {string|string[]}
 */
export function normalizeSupportStatus(status) {
  if (!status) return status;
  if (Array.isArray(status)) {
    return status.map((s) => {
      if (typeof s === "string") {
        const trimmed = s.trim();
        return trimmed.toUpperCase() === "WAITING" ? "WAITING_CUSTOMER" : trimmed;
      }
      return s;
    });
  }
  if (typeof status === "string") {
    return status
      .split(",")
      .map((s) => {
        const trimmed = s.trim();
        return trimmed.toUpperCase() === "WAITING" ? "WAITING_CUSTOMER" : trimmed;
      })
      .join(",");
  }
  return status;
}

/**
 * List platform support tickets across all organizations and public quarantine.
 * @param {Object} [params]
 * @param {string} [params.status] - Comma-separated status list (e.g. "OPEN,IN_PROGRESS", "WAITING")
 * @param {string} [params.priority] - Comma-separated priority list (e.g. "URGENT,HIGH")
 * @param {string} [params.category] - Category filter (e.g. "TECHNICAL,BILLING")
 * @param {string} [params.channel] - Channel filter (e.g. "WEB,CHAT_WIDGET")
 * @param {string} [params.submittingOrganizationId] - Filter by submitting organization ID
 * @param {string} [params.assignedTo] - "me", "unassigned", or operator ID
 * @param {string} [params.search] - Search text
 * @param {boolean} [params.isSlaBreached] - Filter for breached SLA tickets
 * @param {number} [params.page=1] - 1-based page number
 * @param {number} [params.limit=20] - Page size
 * @returns {Promise<{items: Array, total: number, page: number, limit: number}>}
 */
export async function listPlatformTickets(params = {}) {
  const queryParams = { ...params };
  if (params.status) {
    queryParams.status = normalizeSupportStatus(params.status);
  }
  if (params.submittingOrganizationId) {
    queryParams.submittingOrganizationId = params.submittingOrganizationId;
  }
  if (params.assignedTo) {
    queryParams.assignedTo = params.assignedTo;
  }
  if (params.isSlaBreached !== undefined) {
    queryParams.isSlaBreached = params.isSlaBreached;
  }
  const response = await platformClient.get("/support/tickets", { params: queryParams });
  return response.data;
}

export const getPlatformTickets = listPlatformTickets;

/**
 * Get platform support operational KPIs.
 * @returns {Promise<{totalOpen: number, urgentCount: number, slaBreachedCount: number, averageResponseTimeHours: number|null, averageResolutionTimeHours: number|null, casesByCategory: Object, casesByChannel: Object}>}
 */
export async function getPlatformSupportMetrics() {
  const response = await platformClient.get("/support/metrics");
  return response.data;
}

export const getPlatformTicketMetrics = getPlatformSupportMetrics;

/**
 * Get detailed platform ticket including message timeline and internal notes.
 * @param {string} ticketId
 * @returns {Promise<Object>}
 */
export async function getPlatformTicket(ticketId) {
  const response = await platformClient.get(`/support/tickets/${ticketId}`);
  return response.data;
}

/**
 * Update platform ticket metadata (priority, category, subject, resolutionNotes, etc.).
 * @param {string} ticketId
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export async function updatePlatformTicket(ticketId, payload) {
  const data = { ...payload };
  if (data.status) {
    data.status = normalizeSupportStatus(data.status);
  }
  const response = await platformClient.put(`/support/tickets/${ticketId}`, data);
  return response.data;
}

/**
 * Transition platform ticket status.
 * @param {string} ticketId
 * @param {Object|string} payload - Object with status and optional resolutionNotes, or status string
 * @returns {Promise<Object>}
 */
export async function updatePlatformTicketStatus(ticketId, payload) {
  const rawData = typeof payload === "string" ? { status: payload } : { ...payload };
  const data = {
    ...rawData,
    status: normalizeSupportStatus(rawData.status),
  };
  const response = await platformClient.patch(`/support/tickets/${ticketId}/status`, data);
  return response.data;
}

/**
 * Assign platform ticket to platform operator.
 * @param {string} ticketId
 * @param {string|null|Object} assigneeOrPayload - Operator user ID or null to unassign, or { assignedToId }
 * @returns {Promise<Object>}
 */
export async function assignPlatformTicket(ticketId, assigneeOrPayload) {
  const data =
    typeof assigneeOrPayload === "object" && assigneeOrPayload !== null
      ? assigneeOrPayload
      : { assignedToId: assigneeOrPayload };
  const response = await platformClient.patch(`/support/tickets/${ticketId}/assign`, data);
  return response.data;
}

/**
 * Post an outbound reply to submitter or record an internal staff note.
 * @param {string} ticketId
 * @param {Object} payload
 * @param {string} payload.content - Message body
 * @param {boolean} [payload.isInternal=false] - True for confidential internal staff note
 * @param {string} [payload.direction] - INBOUND, OUTBOUND, INTERNAL
 * @param {string} [payload.channel]
 * @param {Array} [payload.attachments]
 * @returns {Promise<Object>}
 */
export async function addPlatformTicketMessage(ticketId, payload) {
  const response = await platformClient.post(`/support/tickets/${ticketId}/messages`, {
    content: payload.content,
    isInternal: Boolean(payload.isInternal),
    direction: payload.direction || (payload.isInternal ? "INTERNAL" : "OUTBOUND"),
    channel: payload.channel || "WEB",
    attachments: payload.attachments || null,
  });
  return response.data;
}

/**
 * List message timeline for a platform ticket.
 * @param {string} ticketId
 * @returns {Promise<Array>}
 */
export async function listPlatformTicketMessages(ticketId) {
  const response = await platformClient.get(`/support/tickets/${ticketId}/messages`);
  return response.data;
}

export const getPlatformTicketMessages = listPlatformTicketMessages;

/**
 * Fetch organization profile data for submitting org context.
 * @param {string} orgId
 * @returns {Promise<Object>}
 */
export async function getPlatformOrganizationData(orgId) {
  const response = await platformClient.get(`/organizations/${orgId}/data`);
  return response.data;
}

export const supportApi = {
  normalizeSupportStatus,
  listPlatformTickets,
  getPlatformTickets,
  getPlatformSupportMetrics,
  getPlatformTicketMetrics,
  getPlatformTicket,
  updatePlatformTicket,
  updatePlatformTicketStatus,
  assignPlatformTicket,
  addPlatformTicketMessage,
  listPlatformTicketMessages,
  getPlatformTicketMessages,
  getPlatformOrganizationData,
};

export default supportApi;
